<!--
  @component
-->
<svelte:options
  customElement={{
    tag: "oc-text-field-v1",
    props: {
      type: { type: "String" },
      placeholder: { type: "String" },
      name: { type: "String" },
      value: { type: "String", reflect: true },
      label: { type: "String", reflect: true },
      hint: { type: "String", reflect: true },
      status: { type: "String", reflect: true },
      required: { type: "Boolean", reflect: true },
      disabled: { type: "Boolean"},
    },
    extend: (customElementConstructor) => {
      return class extends customElementConstructor {
        /*                                          */
        static formAssociated = true;

        constructor() {
          super();
          /*                             */
          this._hostElement = this;
          /*                                */
          this._internals = this.attachInternals();
        }

        /*                             */
        formResetCallback() {
          this.valueReset();
        }

        /*                             */
        formDisabledCallback(isDisabled) {
          this.disabled = isDisabled;
        }

        /*                             */
        formStateRestoreCallback(state, mode) {
          /*   */
        }
      };
    },


  }}
/>

<script lang="ts">
  import { usePropertyChange } from "../../../../common/utils/usePropertyChange";
  import { onMount } from "svelte";

  usePropertyChange();

  export let type: "text" | "email" = "text";
  export let placeholder: string | undefined;
  export let name: string | undefined;
  export let value: string | undefined;
  export let label: string | undefined;
  export let hint: string | undefined;
  export let status: "error" | "success" | "warning" | undefined;
  export let required: boolean | undefined;
  export let disabled: boolean | undefined;

  export let validity;
  export let validationMessage: string;
  export let willValidate: boolean;
  export let _hostElement;
  export let _internals: ElementInternals;


  /*                                         */
  const initialValue = value;
  /*                               */
  let inputElement;

  onMount(() => {
    inputElement = _hostElement.shadowRoot.getElementById("input");
  });

  /*                                      */
  $: {
    _internals.setFormValue(value);
    if (inputElement) {
      _internals.setValidity(inputElement.validity, inputElement.validationMessage, inputElement);
    }
    validationMessage = _internals.validationMessage;
  }

  /*                            */
  $: _internals.ariaDescription = hint;

  /*                                                             */
  $: validity = _internals.validity;
  $: willValidate = _internals.willValidate;


  /*                                    */
  const onChange = (event) => {
    value = event.target.value;
  };

  /*                            */
  export const valueReset = () => {
    value = initialValue;
  };

  /*                                                    */
  export function checkValidity() {
    return _internals.checkValidity();
  }

</script>

<div
  class="textfield"
  class:textfield--error={status === "error"}
  class:textfield--warning={status === "warning"}
  class:textfield--success={status === "success"}
  class:textfield--disabled={disabled}
>
  <input
    class="textfield__input"
    {name}
    {type}
    {placeholder}
    {value}
    {required}
    {disabled}
    id="input"
    autocomplete="off"
    on:change={onChange}/>
  <label class="textfield__label" for="input" id="label">{label}</label>
  {#if hint}
    <small class="textfield__hint">{hint}</small>
  {/if}
</div>

<style lang="scss">

  $color: #222;
  $color-error: #f00020;
  $color-success: #50cc7f;
  $color-warning: #ffdf33;
  $color-disabled: #c4c4c4;

  .textfield {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    color: $color;
    font-family: OTTOSans, Arial, Helvetica, sans-serif;
    font-size: 14px;
    border: 0;
    margin: 0;
    padding: 0;
    margin-top: 6px;
    margin-bottom: 24px;

    &--error {
      .textfield__input {
        border-color: $color-error;
        color: $color-error;
      }

      .textfield__label {
        color: $color-error;
      }

      .textfield__hint {
        color: $color-error;
      }
    }


    &--warning {
      .textfield__input {
        border-color: $color-warning;
      }

      .textfield__label {
        color: $color-warning;
      }

      .textfield__hint {
        color: $color-warning;
      }
    }

    &--success {
      .textfield__input {
        border-color: $color-success;
        color: $color-success;
      }

      .textfield__label {
        color: $color-success;
      }

      .textfield__hint {
        color: $color-success;
      }
    }

    &--disabled {
      .textfield__input {
        border-color: $color-disabled;
        color: $color-disabled;
      }

      .textfield__label {
        color: $color-disabled;
      }

      .textfield__hint {
        color: $color-disabled;
      }
    }

    &__input {
      position: relative;
      height: 48px;
      padding: 0 16px;
      width: 100%;
      display: block;
      font-size: 1rem;
      line-height: 1.5;
      border: 1px solid $color-disabled;
      border-radius: 8px;
      color: $color;
      background: #fff;
    }

    &__label {
      font-family: OTTOSans, Arial, Helvetica, sans-serif;
      box-sizing: border-box;
      border: 0;
      padding: 0 8px;
      background-color: #fff;
      position: absolute;
      user-select: none;
      overflow: hidden;
      color: #777;
      display: inline-block;
      margin: 0;
      font-size: 1rem;
      line-height: 1.5;
      max-width: calc(100% - 16px);
      top: 0;
      left: 0;
      white-space: nowrap;
      text-overflow: ellipsis;
      cursor: text;
      border-radius: 8px;
      width: auto;

      transition: transform .2s cubic-bezier(.4, 0, .2, 1);
      transform-origin: 0 0;
      transform: translate(8px, 12px) scale(1);
    }

    &__hint {
      display: block;
      margin: 4px 0 0;
      font-size: .75rem;
      line-height: 1.33;
    }

    &__input:focus + .textfield__label,
    &__input:not(:placeholder-shown) + .textfield__label {
      transform: translate(11px, -8px) scale(.75);
    }

  }
</style>